import * as React from 'react';
import {
  view,
  dynamic,
  AnimatePresence,
  createViewConfig,
  keyframes,
  assignProps,
} from '@sail/engine';
import {cancel, warningCircle} from '@sail/icons/react/Icon';
import {motion, useReducedMotion} from '@sail/motion';
import {tokens} from '@sail/tokens';
import {Icon, IconConfig} from '../Icon';
import {ToastLayer} from './ToastLayer';
import type {ToastProps} from './types';
import {useAnimatedLayerIntents, useControlledTimer} from './hooks';
import {
  ProgressIndicator,
  ProgressIndicatorConfig,
  type ProgressIndicatorStatus,
} from '../ProgressIndicator';
import {ToastButton} from './ToastButton';
import {css} from '../../css';

const spinAnimation = keyframes({
  '0%': {transform: 'rotate(0deg)'},
  '100%': {transform: 'rotate(360deg)'},
});

export const ToastConfig = createViewConfig({
  name: 'Toast',
  props: {} as ToastProps,
  defaults: {
    type: 'neutral',
    placement: 'bottom',
  },
});

/**
 * Toasts display a message to the user.
 *
 * By default each `<Toast />` is rendered in a separate isolated "toast" layer.
 *
 * Stacking of toasts can be achieved by wrapping with `<ToastLayer />`.
 */
export const Toast = ToastConfig.createView(
  ({
    type,
    isOpen,
    children,
    subviews,
    onDismiss,
    actionLabel,
    onActionClick,
    duration = 'short',
    autoDismiss = true,
    placement = 'bottom',
    progress,
    revision,
    ...props
  }) => {
    const animationIntents = useAnimatedLayerIntents({
      placement,
    });

    const hasIcon = type === 'error' || type === 'pending';
    const hasAction = onActionClick && actionLabel;
    const isPendingComplete =
      type === 'pending' && progress != null && progress >= 100;
    const hasDismiss = type !== 'pending' || isPendingComplete;

    const shouldAutoClose =
      autoDismiss && (type === 'neutral' || isPendingComplete);

    const timer = useControlledTimer({
      duration: duration === 'short' ? 5_000 : 8_000,
      onComplete: onDismiss,
    });

    React.useEffect(() => {
      if (!shouldAutoClose) {
        return;
      }

      if (isOpen) {
        timer.start();

        return () => {
          timer.reset();
        };
      }
      // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
    }, [
      isOpen,
      shouldAutoClose,
      progress,
      type,
      autoDismiss,
      duration,
      // Purposely included in the dependency array to trigger a reset of the
      // timer when the revision number changes
      revision,
    ]);

    const handleMouseEnter = () => {
      if (shouldAutoClose) {
        timer.pause();
      }
    };

    const handleMouseLeave = () => {
      if (shouldAutoClose) {
        timer.resume();
      }
    };

    let pendingProgressStatus: ProgressIndicatorStatus;
    if (progress === 0) {
      pendingProgressStatus = 0;
    } else if (progress == null) {
      pendingProgressStatus = 15;
    } else {
      pendingProgressStatus = progress >= 100 ? 'complete' : progress;
    }

    const spinPendingIconWrapper =
      progress == null && pendingProgressStatus !== 'complete';

    const prefersReducedMotion = useReducedMotion();
    const layoutMotionIntent = prefersReducedMotion
      ? null
      : motion({
          layout: true,
          transition: {
            layout: {duration: 0.08},
          },
        });

    return (
      <AnimatePresence>
        {isOpen ? (
          <ToastLayer placement={placement}>
            <view.div
              role="alert"
              aria-live="polite"
              uses={[
                ...animationIntents,
                ...(layoutMotionIntent ? [layoutMotionIntent] : []),
              ]}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              {...props}
            >
              {hasIcon ? (
                <view.div
                  uses={[motion({layout: 'position'})]}
                  css={{
                    stack: 'x',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    marginRight: 'small',
                    justifyContent: 'center',
                  }}
                >
                  {type === 'error' ? (
                    <Icon icon={warningCircle} />
                  ) : (
                    <view.div
                      css={[
                        spinPendingIconWrapper
                          ? css({
                              animation: `${spinAnimation} 1s linear infinite`,
                            })
                          : css({animation: 'none'}),
                        css({
                          stack: 'x',
                          alignX: 'center',
                          alignY: 'center',
                        }),
                      ]}
                    >
                      <ProgressIndicator
                        size="medium"
                        status={pendingProgressStatus}
                        subviews={{
                          donut: {
                            uses: [
                              assignProps({
                                foregroundColor: tokens['color.hue.slate.0'],
                                backgroundColor: `color-mix(in srgb, ${tokens['color.hue.slate.0']}, transparent 70%)`,
                              }),
                            ],
                          },
                          icon: {
                            css: {
                              fill: tokens['color.neutral.0'],
                              backgroundColor: tokens['color.neutral.800'],
                            },
                          },
                        }}
                      />
                    </view.div>
                  )}
                </view.div>
              ) : null}
              <view.div
                uses={[motion({layout: 'position'})]}
                inherits={subviews.message}
              >
                {children}
              </view.div>
              {hasAction ? (
                <ToastButton
                  inherits={subviews.action}
                  onClick={onActionClick}
                  uses={[motion({layout: 'position'})]}
                >
                  {actionLabel}
                </ToastButton>
              ) : null}
              {hasDismiss ? (
                <ToastButton inherits={subviews.dismiss} onClick={onDismiss}>
                  <Icon
                    uses={[motion({layout: 'position'})]}
                    icon={cancel}
                    size="xsmall"
                  />
                </ToastButton>
              ) : null}
            </view.div>
          </ToastLayer>
        ) : null}
      </AnimatePresence>
    );
  },
  {
    css: {
      stack: 'x',
      width: 'fit',
      height: 'fill',
      alignItems: 'center',
      maxWidth: '576px',
      boxShadow: 'large',
      borderRadius: 'small',
      paddingLeft: 'medium',
      position: 'relative',

      font: 'body.small',
    },
    uses: [
      dynamic((props) => {
        if (props.type !== 'error') {
          return [
            css({
              color: 'onFeedback.neutral.inverted',
              backgroundColor: 'feedback.neutral.inverted',
            }),
            IconConfig.customize({
              css: {
                fill: 'onFeedback.neutral.inverted',
              },
            }),
            ProgressIndicatorConfig.customize({
              css: {
                fill: 'onFeedback.neutral.inverted',
              },
            }),
          ];
        }

        return [
          css({
            color: 'onFeedback.critical.emphasized',
            backgroundColor: 'feedback.critical.emphasized',
          }),
          IconConfig.customize({
            css: {
              fill: 'onFeedback.critical.emphasized',
            },
          }),
          ProgressIndicatorConfig.customize({
            css: {
              fill: 'onFeedback.critical.emphasized',
            },
          }),
        ];
      }),
    ],
    subviews: {
      message: {
        css: {
          width: 'fill',
          marginY: 'small',
          paddingY: 'small',
          paddingRight: 'medium',
          fontVariantNumeric: 'tabular-nums',
        },
      },
      action: {
        css: {
          marginX: 'small',
          paddingX: 'small',
          paddingY: 'xsmall',
          borderRadius: 'action',

          font: 'label.medium.emphasized',
        },
      },
      dismiss: {
        css: {
          alignSelf: 'stretch',

          borderTopRightRadius: 'action',
          borderBottomRightRadius: 'action',

          borderLeft: '1px solid',
          borderLeftColor: `color-mix(in srgb, ${tokens['color.border']}, transparent 75%)`,
        },
      },
    },
  },
);
